import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import errMsg from "../../services/errMsg";

export const fetchCompanyUsers = createAsyncThunk(
  "companyUsers/fetch",
  async ({ companyId, params = {} }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/companies/${companyId}/users`, { params });
      return { companyId, ...data.data };
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load users"));
    }
  }
);

export const createCompanyUser = createAsyncThunk(
  "companyUsers/create",
  async ({ companyId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/companies/${companyId}/users`, payload);
      return data.data.user;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to create user"));
    }
  }
);

export const updateCompanyUser = createAsyncThunk(
  "companyUsers/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/users/${id}`, payload);
      return data.data.user;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to update user"));
    }
  }
);

export const deleteCompanyUser = createAsyncThunk(
  "companyUsers/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/users/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to delete user"));
    }
  }
);

const initialState = {
  companyId: null,
  items: [],
  pagination: { page: 1, limit: 25, total: 0, pages: 0 },
  query: { page: 1, limit: 25, search: "", includeDeleted: false },
  loading: false,
  saving: false,
  error: null,
};

const slice = createSlice({
  name: "companyUsers",
  initialState,
  reducers: {
    setQuery(state, { payload }) {
      state.query = { ...state.query, ...payload };
    },
    resetForCompany(state, { payload }) {
      if (state.companyId !== payload) {
        state.companyId = payload;
        state.items = [];
        state.pagination = initialState.pagination;
        state.query = { ...initialState.query };
        state.error = null;
      }
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchCompanyUsers.pending, (s) => {
      s.loading = true;
      s.error = null;
    })
      .addCase(fetchCompanyUsers.fulfilled, (s, a) => {
        s.loading = false;
        s.companyId = a.payload.companyId;
        s.items = a.payload.items;
        s.pagination = a.payload.pagination;
      })
      .addCase(fetchCompanyUsers.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(createCompanyUser.pending, (s) => {
        s.saving = true;
      })
      .addCase(createCompanyUser.fulfilled, (s) => {
        s.saving = false;
      })
      .addCase(createCompanyUser.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      })
      .addCase(updateCompanyUser.pending, (s) => {
        s.saving = true;
      })
      .addCase(updateCompanyUser.fulfilled, (s, a) => {
        s.saving = false;
        const idx = s.items.findIndex((u) => u._id === a.payload._id);
        if (idx >= 0) s.items[idx] = { ...s.items[idx], ...a.payload };
      })
      .addCase(updateCompanyUser.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      })
      .addCase(deleteCompanyUser.fulfilled, (s, a) => {
        s.items = s.items.filter((u) => u._id !== a.payload);
        s.pagination.total = Math.max(0, s.pagination.total - 1);
      })
      .addCase(deleteCompanyUser.rejected, (s, a) => {
        s.error = a.payload;
      });
  },
});

export const { setQuery, resetForCompany, clearError } = slice.actions;
export default slice.reducer;
